import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from './ui/dialog';
import { toast } from 'sonner';
import { 
  FileText, 
  Star, 
  Clock, 
  CheckCircle, 
  MessageSquare,
  Download,
  ExternalLink,
  AlertTriangle,
  Loader2,
  Shield
} from 'lucide-react';
import { motion } from 'framer-motion';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ClientReviewPortal() {
  const { reportId } = useParams();
  const [loading, setLoading] = useState(true);
  const [portalData, setPortalData] = useState(null);
  const [showAmendmentDialog, setShowAmendmentDialog] = useState(false);
  const [amendmentComments, setAmendmentComments] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchPortalData();
  }, [reportId]);

  const fetchPortalData = async () => {
    try {
      const response = await axios.get(`${API}/review-workflow/client-portal/${reportId}`);
      setPortalData(response.data);
    } catch (error) {
      console.error('Failed to fetch portal data:', error);
      toast.error('Unable to load report data');
    } finally {
      setLoading(false);
    }
  };

  const handleAction = async (action, platform = null) => {
    setSubmitting(true);
    try {
      await axios.post(`${API}/review-workflow/client-portal/${reportId}/action`, {
        report_id: reportId,
        action: action,
        platform: platform,
        amendment_comments: action === 'request_amendments' ? amendmentComments : null
      });
      
      if (action === 'leave_review') {
        // Open review link
        const reviewUrl = platform === 'google' 
          ? portalData.google_review_url 
          : portalData.trustpilot_review_url;
        if (reviewUrl) {
          window.open(reviewUrl, '_blank');
        }
        toast.success('Thank you! Your support means a lot to us.');
      } else if (action === 'request_amendments') {
        setShowAmendmentDialog(false);
        setAmendmentComments('');
        toast.success('Amendment request submitted successfully');
      } else if (action === 'maybe_later') {
        toast.info('No problem! We\'ll send a gentle reminder soon.');
      } else if (action === 'approve_final') {
        toast.success('Report approved! The final version will be prepared shortly.');
      }
      
      fetchPortalData();
    } catch (error) {
      console.error('Action failed:', error);
      toast.error('Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const submitAmendment = async () => {
    if (!amendmentComments.trim()) {
      toast.error('Please provide details about the required amendments');
      return;
    }
    await handleAction('request_amendments');
  };

  const handleDownloadDraft = async (format = 'pdf') => {
    setSubmitting(true);
    try {
      toast.info('Preparing draft report for download...');
      const downloadUrl = `${API}/review-workflow/client-portal/${reportId}/download-draft?format=${format}`;
      
      const response = await fetch(downloadUrl);
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || 'Failed to download draft report');
      }
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      
      // Get filename from Content-Disposition header or use default
      const contentDisposition = response.headers.get('Content-Disposition');
      let filename = `Draft_Report_${reportId.substring(0, 8)}.${format}`;
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="?([^";\n]+)"?/);
        if (filenameMatch) {
          filename = filenameMatch[1];
        }
      }
      
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success('Draft report downloaded successfully!');
    } catch (error) {
      console.error('Download failed:', error);
      toast.error(error.message || 'Failed to download draft report');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDownloadFinal = async (format = 'pdf') => {
    setSubmitting(true);
    try {
      toast.info('Preparing final report for download...');
      const downloadUrl = `${API}/review-workflow/client-portal/${reportId}/download-final?format=${format}`;
      
      const response = await fetch(downloadUrl);
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || 'Failed to download final report');
      }
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      
      // Get filename from Content-Disposition header or use default
      const contentDisposition = response.headers.get('Content-Disposition');
      let filename = `Final_Report_${reportId.substring(0, 8)}.${format}`;
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="?([^";\n]+)"?/);
        if (filenameMatch) {
          filename = filenameMatch[1];
        }
      }
      
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success('Final report downloaded successfully!');
    } catch (error) {
      console.error('Download failed:', error);
      toast.error(error.message || 'Failed to download final report');
    } finally {
      setSubmitting(false);
    }
  };

  const getStatusInfo = (status) => {
    const statusMap = {
      'Draft': { 
        label: 'Draft Report Available', 
        color: 'bg-blue-100 text-blue-800',
        icon: FileText,
        description: 'Your draft report is ready for review'
      },
      'Review Requested': { 
        label: 'Awaiting Your Review', 
        color: 'bg-amber-100 text-amber-800',
        icon: Star,
        description: 'Please review the draft and let us know your thoughts'
      },
      'Amendments Requested': { 
        label: 'Amendments in Progress', 
        color: 'bg-orange-100 text-orange-800',
        icon: MessageSquare,
        description: 'We are working on the requested changes'
      },
      'Approved - Pending Final': { 
        label: 'Preparing Final Report', 
        color: 'bg-purple-100 text-purple-800',
        icon: Clock,
        description: 'Your final report is being prepared'
      },
      'Final Issued': { 
        label: 'Final Report Issued', 
        color: 'bg-green-100 text-green-800',
        icon: CheckCircle,
        description: 'Your final report is ready for download'
      }
    };
    return statusMap[status] || statusMap['Draft'];
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-teal-600" />
      </div>
    );
  }

  if (!portalData) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center">
        <Card className="max-w-md">
          <CardContent className="p-8 text-center">
            <AlertTriangle className="h-12 w-12 text-amber-500 mx-auto mb-4" />
            <h2 className="text-xl font-bold mb-2">Report Not Found</h2>
            <p className="text-slate-600">
              This report link may have expired or is invalid. Please contact your surveyor.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const statusInfo = getStatusInfo(portalData.review_status);
  const StatusIcon = statusInfo.icon;
  const showReviewOptions = portalData.review_status === 'Draft' || portalData.review_status === 'Review Requested';
  const canApprove = showReviewOptions && !portalData.has_pending_amendment;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 py-12 px-4" data-testid="client-review-portal">
      <div className="max-w-2xl mx-auto space-y-6">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center"
        >
          <h1 className="text-2xl font-bold text-slate-900">Report Review Portal</h1>
          <p className="text-slate-600 mt-1">{portalData.property_address}</p>
          {portalData.report_reference && (
            <p className="text-sm text-slate-500">Ref: {portalData.report_reference}</p>
          )}
        </motion.div>

        {/* Status Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
        >
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className={`p-3 rounded-full ${statusInfo.color}`}>
                  <StatusIcon className="h-6 w-6" />
                </div>
                <div>
                  <Badge className={statusInfo.color}>{statusInfo.label}</Badge>
                  <p className="text-slate-600 mt-1">{statusInfo.description}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </motion.div>

        {/* Download Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
        >
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileText className="h-5 w-5 text-teal-600" />
                Your Report
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {portalData.draft_available && (
                <div className="space-y-2">
                  <Button 
                    variant={portalData.final_available ? "outline" : "default"}
                    className={!portalData.final_available ? "w-full bg-teal-600 hover:bg-teal-700" : "w-full"}
                    data-testid="download-draft-btn"
                    onClick={() => handleDownloadDraft('pdf')}
                    disabled={submitting}
                  >
                    {submitting ? (
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    ) : (
                      <Download className="h-4 w-4 mr-2" />
                    )}
                    Download Draft Report (PDF) {portalData.final_available && '(Superseded)'}
                  </Button>
                  <Button 
                    variant="outline"
                    className="w-full"
                    data-testid="download-draft-docx-btn"
                    onClick={() => handleDownloadDraft('docx')}
                    disabled={submitting}
                  >
                    {submitting ? (
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    ) : (
                      <Download className="h-4 w-4 mr-2" />
                    )}
                    Download Draft Report (Word)
                  </Button>
                </div>
              )}
              {portalData.final_available && (
                <div className="space-y-2">
                  <Button 
                    className="w-full bg-green-600 hover:bg-green-700" 
                    data-testid="download-final-btn"
                    onClick={() => handleDownloadFinal('pdf')}
                    disabled={submitting}
                  >
                    {submitting ? (
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    ) : (
                      <Download className="h-4 w-4 mr-2" />
                    )}
                    Download Final Report (PDF)
                  </Button>
                  <Button 
                    variant="outline"
                    className="w-full border-green-300 hover:bg-green-50"
                    data-testid="download-final-docx-btn"
                    onClick={() => handleDownloadFinal('docx')}
                    disabled={submitting}
                  >
                    {submitting ? (
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    ) : (
                      <Download className="h-4 w-4 mr-2" />
                    )}
                    Download Final Report (Word)
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        </motion.div>

        {/* Review Options - Only show for Draft/Review Requested status */}
        {showReviewOptions && portalData.review_enabled && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
          >
            <Card className="border-amber-200 bg-amber-50/50">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Star className="h-5 w-5 text-amber-500" />
                  Share Your Feedback (Optional)
                </CardTitle>
                <CardDescription>
                  We welcome honest feedback — positive or constructive.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Review Buttons */}
                <div className="flex flex-col sm:flex-row gap-3">
                  {(portalData.review_platform === 'google' || portalData.review_platform === 'both') && portalData.google_review_url && (
                    <Button 
                      variant="outline" 
                      className="flex-1 border-amber-300 hover:bg-amber-100"
                      onClick={() => handleAction('leave_review', 'google')}
                      disabled={submitting}
                      data-testid="leave-google-review-btn"
                    >
                      <Star className="h-4 w-4 mr-2 text-amber-500" />
                      Leave Google Review
                      <ExternalLink className="h-3 w-3 ml-2" />
                    </Button>
                  )}
                  {(portalData.review_platform === 'trustpilot' || portalData.review_platform === 'both') && portalData.trustpilot_review_url && (
                    <Button 
                      variant="outline" 
                      className="flex-1 border-green-300 hover:bg-green-100"
                      onClick={() => handleAction('leave_review', 'trustpilot')}
                      disabled={submitting}
                      data-testid="leave-trustpilot-review-btn"
                    >
                      <Star className="h-4 w-4 mr-2 text-green-500" />
                      Leave Trustpilot Review
                      <ExternalLink className="h-3 w-3 ml-2" />
                    </Button>
                  )}
                </div>

                {/* Action Buttons */}
                <div className="border-t pt-4 space-y-3">
                  <div className="flex flex-col sm:flex-row gap-3">
                    <Button 
                      variant="outline" 
                      className="flex-1"
                      onClick={() => setShowAmendmentDialog(true)}
                      disabled={submitting}
                      data-testid="request-amendments-btn"
                    >
                      <MessageSquare className="h-4 w-4 mr-2" />
                      Request Amendments
                    </Button>
                    <Button 
                      variant="outline" 
                      className="flex-1"
                      onClick={() => handleAction('maybe_later')}
                      disabled={submitting}
                      data-testid="maybe-later-btn"
                    >
                      <Clock className="h-4 w-4 mr-2" />
                      Maybe Later
                    </Button>
                  </div>
                  
                  {canApprove && (
                    <Button 
                      className="w-full bg-green-600 hover:bg-green-700"
                      onClick={() => handleAction('approve_final')}
                      disabled={submitting}
                      data-testid="approve-final-btn"
                    >
                      {submitting ? (
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      ) : (
                        <CheckCircle className="h-4 w-4 mr-2" />
                      )}
                      Approve & Issue Final Report
                    </Button>
                  )}
                </div>

                {/* Ethical Disclaimer */}
                <div className="flex items-start gap-2 p-3 bg-white rounded-lg text-sm text-slate-600">
                  <Shield className="h-4 w-4 text-teal-600 flex-shrink-0 mt-0.5" />
                  <p>{portalData.ethical_disclaimer}</p>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        )}

        {/* Amendment Pending Notice */}
        {portalData.has_pending_amendment && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <Card className="border-orange-200 bg-orange-50">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <AlertTriangle className="h-5 w-5 text-orange-500" />
                  <div>
                    <p className="font-medium text-orange-800">Amendment Request Pending</p>
                    <p className="text-sm text-orange-700">
                      We are working on your requested changes. You'll be notified when the updated draft is ready.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        )}

        {/* Contact Info */}
        <div className="text-center text-sm text-slate-500">
          <p>Questions about your report? Contact your surveyor directly.</p>
        </div>
      </div>

      {/* Amendment Dialog */}
      <Dialog open={showAmendmentDialog} onOpenChange={setShowAmendmentDialog}>
        <DialogContent data-testid="amendment-dialog">
          <DialogHeader>
            <DialogTitle>Request Amendments</DialogTitle>
            <DialogDescription>
              Please describe the changes you'd like us to make to your report.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Textarea
              value={amendmentComments}
              onChange={(e) => setAmendmentComments(e.target.value)}
              placeholder="Please specify the sections or details that need to be amended..."
              rows={5}
              data-testid="amendment-comments-input"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAmendmentDialog(false)}>
              Cancel
            </Button>
            <Button 
              onClick={submitAmendment} 
              disabled={submitting || !amendmentComments.trim()}
              data-testid="submit-amendment-btn"
            >
              {submitting ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <MessageSquare className="h-4 w-4 mr-2" />
              )}
              Submit Request
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
